import { hasAccessToPackage } from "app/common/services/solutions2Helper";
import { swSettings } from "app/common/services/swSettings";
import { getBaseUrl } from "app/components/sidebar3/primary-side-bar/utils";
import { FORCE_PRO_PARAM } from "single-spa/react-app/router/NavigationUtils";

import type { IAvailableSolutions, ISubscriptionSolution, IUserData } from "./solutionBaseInterfaces";

const getCurrentSolutionUsers = (solutionName: string): IUserData[] => {
    const result: IUserData[] = [];
    swSettings.subscriptionSolutions?.forEach((subscriptionSolution) => {
        if (subscriptionSolution.Solutions.includes(solutionName)) {
            result.push({ userId: subscriptionSolution.UserId, name: subscriptionSolution.AccountName, solutionName });
        }
    });
    return result;
};
export const getReturnUrl = (sol: IAvailableSolutions): string | (() => void) => {
    const { isInMySolutions, HookState, url } = sol;
    return typeof url === "function"
        ? // @ts-ignore FIXME: strict migration - call arg lacks full ISolutionNavigation shape
          url({
              isInMySolutions,
              HookState,
          })
        : url;
};

export const getSwitchAccountUrl = (sol: IAvailableSolutions, returnUrl?: string): string | null => {
    const { isInMySolutions, usersInMySolution } = sol;
    if (!isInMySolutions && usersInMySolution.length > 0) {
        return buildSwitchAccountUrl(
            getBaseUrl(swSettings.swsites.login_handler),
            usersInMySolution[0].userId,
            returnUrl ?? getReturnUrl(sol),
        );
    }
    return null;
};

export const buildSwitchAccountUrl = (
    baseUrl: string,
    userId: string | number,
    returnUrl: string | (() => void),
): string => {
    return `${baseUrl}/account/user/switch?userId=${userId}&returnUrl=${returnUrl}`;
};

const linkTarget = "_blank";

export const getAvailableSolutions = (): IAvailableSolutions[] => [
    {
        solutionTitle: "settings.solution_switcher.web.title",
        solutionText: "settings.solution_switcher.web.text",
        linkTarget,
        url: `${swSettings.swsites.pro}?${FORCE_PRO_PARAM}=true`,
        itemKey: "web-solution",
        isInMySolutions: swSettings.user.hasMR || swSettings.user.hasDM,
        HookState: "WebMarketAnalysisOverviewHomepage",
        trackingKey: "sidebar.solutions.DRI",
        usersInMySolution: getCurrentSolutionUsers("web-solution"),
    },
    {
        solutionTitle: "settings.solution_switcher.app.title",
        solutionText: "settings.solution_switcher.app.text",
        linkTarget,
        url: "#/digitalsuite/companyresearch/appanalysis/home",
        itemKey: "app-solution",
        isInMySolutions: hasAccessToPackage("appIntelligence"),
        trackingKey: "sidebar.solutions.app",
        usersInMySolution: getCurrentSolutionUsers("app-solution"),
    },
    {
        solutionTitle: "settings.solution_switcher.shopper.title",
        solutionText: "settings.solution_switcher.shopper.text",
        linkTarget,
        url: swSettings.swsites.shopper,
        itemKey: "shopper-solution",
        isInMySolutions: swSettings.user.applications.includes("Datafactory"),
        trackingKey: "sidebar.solutions.shopper",
        usersInMySolution: getCurrentSolutionUsers("shopper-solution"),
    },
    {
        solutionTitle: "settings.solution_switcher.sales.title",
        solutionText: "settings.solution_switcher.sales.text",
        linkTarget,
        url: "#/sales",
        itemKey: "sales-solution",
        isInMySolutions: hasAccessToPackage("salesIntelligence"),
        trackingKey: "sidebar.solutions.SI",
        usersInMySolution: getCurrentSolutionUsers("sales-solution"),
    },
    {
        solutionTitle: "settings.solution_switcher.stock.title",
        solutionText: "settings.solution_switcher.stock.text",
        linkTarget,
        url: swSettings.swsites.investors,
        itemKey: "stock-solution",
        isInMySolutions: swSettings.user.applications.includes("Investors"),
        trackingKey: "sidebar.solutions.stock",
        usersInMySolution: getCurrentSolutionUsers("stock-solution"),
    },
    {
        solutionText: "",
        solutionTitle: "settings.solution_switcher.data_exporter.title",
        linkTarget,
        url: swSettings.swsites.datahub,
        itemKey: "api-solution",
        isInMySolutions: true,
        trackingKey: "sidebar.solutions.API",
        usersInMySolution: getCurrentSolutionUsers("api-solution"),
    },
];

export const isSolutionInMyUserSubscription = (
    subscriptionSolutions: ISubscriptionSolution[],
    myUserId: number,
    mySolution: string,
): boolean => {
    if (!subscriptionSolutions || !Array.isArray(subscriptionSolutions) || !myUserId) {
        return false;
    }

    const mySubscription = subscriptionSolutions.find((sub) => sub.UserId === myUserId);

    if (mySubscription?.Solutions.includes(mySolution)) {
        return true;
    }

    return false;
};

export const isSolutionInMyOtherSubscriptions = (
    subscriptionSolutions: ISubscriptionSolution[],
    mySolution: string,
): boolean => {
    if (!subscriptionSolutions || !Array.isArray(subscriptionSolutions) || !mySolution) {
        return false;
    }

    return subscriptionSolutions.some((sub) => sub.Solutions.includes(mySolution));
};
